import { Text, TextField, Toggle } from "@merge-api/merge-javascript-shared";
import { Trash2 } from "lucide-react";
import { ConductorFlagRule } from "../types";
import TenantSelectField from "./TenantSelectField";
import UuidChipsField from "./UuidChipsField";

interface Props {
  index: number;
  rule: ConductorFlagRule;
  onChange: (rule: ConductorFlagRule) => void;
  onRemove: () => void;
}

// One OR branch of a flag's ruleset. Every populated dimension inside this card
// is AND'd, so leaving one empty means it does not constrain the match.
const ConductorFlagRuleCard = ({ index, rule, onChange, onRemove }: Props) => {
  const patch = (changes: Partial<ConductorFlagRule>) => onChange({ ...rule, ...changes });

  const onPercentChange = (raw: string) => {
    if (raw.trim() === "") {
      patch({ percent: null });
      return;
    }
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) patch({ percent: parsed });
  };

  const percentInvalid = rule.percent !== null && (rule.percent < 0 || rule.percent > 100);

  return (
    <div className="mb-3 rounded-lg border border-gray-10 bg-white">
      <div className="flex items-center justify-between border-b border-gray-10 px-4 py-2">
        <div className="flex items-center gap-3">
          <Text variant="h6">Rule {index + 1}</Text>
          <Text variant="sm" className="text-gray-40">
            all populated fields must match
          </Text>
        </div>
        <div className="flex items-center gap-3">
          <Text variant="sm" className="text-gray-60">
            Enabled
          </Text>
          <Toggle
            checked={rule.enabled !== false}
            onChange={(value) => patch({ enabled: value })}
          />
          <button
            onClick={onRemove}
            aria-label={`Remove rule ${index + 1}`}
            className="rounded p-1 text-red-60 hover:bg-gray-10"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-3 p-4">
        <UuidChipsField
          label="Organizations"
          values={rule.organization_ids}
          onChange={(organization_ids) => patch({ organization_ids })}
          placeholder="paste organization UUIDs"
        />
        <UuidChipsField
          label="Integrations"
          values={rule.integration_ids}
          onChange={(integration_ids) => patch({ integration_ids })}
          placeholder="paste integration UUIDs"
        />
        <UuidChipsField
          label="Linked accounts"
          values={rule.linked_account_ids}
          onChange={(linked_account_ids) => patch({ linked_account_ids })}
          placeholder="paste linked account UUIDs"
        />
        <UuidChipsField
          label="Blueprints"
          values={rule.blueprint_ids}
          onChange={(blueprint_ids) => patch({ blueprint_ids })}
          placeholder="paste blueprint UUIDs"
        />
        <TenantSelectField values={rule.tenants} onChange={(tenants) => patch({ tenants })} />

        <div className="flex items-start gap-4">
          <div className="w-32">
            <TextField
              labelTitle="Percent"
              type="number"
              value={rule.percent === null ? "" : String(rule.percent)}
              onChange={(event) => onPercentChange(event.target.value)}
              placeholder="all"
              error={percentInvalid}
              errorText={percentInvalid ? "0 to 100" : undefined}
            />
          </div>
          <div className="flex-1">
            <TextField
              labelTitle="Description"
              value={rule.description}
              onChange={(event) => patch({ description: event.target.value })}
              placeholder="what this rule is for"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConductorFlagRuleCard;
